import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import OTPInput from "react-otp-input";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ToastContainer } from "react-toastify";
import { ClipLoader } from "react-spinners";
import { apiHeader, postData } from "../../helpers/ApiHelper";
import {
  setEncodedCookie,
  toasterrormsg,
  toastsuccessmsg,
} from "../../helpers/Reusable";
import { auth } from "../../firebase";
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  PhoneAuthProvider,
  signInWithCredential,
} from "firebase/auth";
import AuthBg from "./AuthBg";

const OtpVerify = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { field, token, userId, verificationId, isEmailField } =
    location.state || {};
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(120); // ⏱️ 2 minutes
  const [canResend, setCanResend] = useState(true);

  // Formik InitialValues
  const initialValues = {
    otp: "",
  };

  // Fromik Validation Schema
  const schema = Yup.object().shape({
    otp: Yup.string()
      .matches(/^\d{6}$/, "OTP Is Invalid")
      .required("OTP Is Required"),
  });

  const formik = useFormik({
    initialValues,
    validationSchema: schema,
    onSubmit: async (values, { resetForm }) => {
      setLoading(true);
      if (isEmailField) {
        const param = {
          email: field,
          otp: values.otp,
        };
        const response = await postData(
          "user/auth/public/verifyotp",
          param,
          apiHeader(false)
        );
        if (
          String(response?.status) == "200" &&
          String(response?.data.status) == "200"
        ) {
          const data = response.data.data;
          toastsuccessmsg(response.data.message);
          setTimeout(() => {
            setEncodedCookie("token", data?.token, {});
            setEncodedCookie("uid", data?.userId, {});
            setLoading(false);
            navigate("/home");
            resetForm();
          }, 1500);
        } else {
          setLoading(false);
          toasterrormsg(response.data.message);
        }
      } else {
        otpVerify(token, userId, verificationId, values.otp);
      }
    },
  });

  const sendEmailOtp = async (field) => {
    const param = {
      field: field,
    };

    const response = await postData(
      "user/auth/public/checkemailornumber",
      param,
      apiHeader(false)
    );

    if (
      String(response?.status) == "200" &&
      String(response?.data.status) == "200"
    ) {
      const data = response.data.data;
      toastsuccessmsg(response.data.message);
      formik.resetForm();
    } else {
      toasterrormsg(response.data.message);
    }
  };

  useEffect(() => {
    if (timer == 0) {
      setCanResend(false); // 🟢 Enable resend when timer hits 0
      return;
    }

    const interval = setInterval(() => {
      setTimer((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval); // Clean on unmount or re-run
  }, [timer]);

  // Format MM:SS
  const formatTime = () => {
    const minutes = Math.floor(timer / 60)
      .toString()
      .padStart(2, "0");
    const seconds = (timer % 60).toString().padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  const handleResend = () => {
    if (timer == 0 && isEmailField) {
      sendEmailOtp(field);
      setTimer(120); // reset to 2 minutes
      setCanResend(true); // 🟢 Enable resend when timer hits 0
    }
  };

  const handleOtpChange = (value) => {
    formik.setFieldValue("otp", value);
  };

  // Mobile Number Otp verify (Firebase v9+)
  const otpVerify = async (token, userId, verificationId, otp) => {
    try {
      const credential = PhoneAuthProvider.credential(verificationId, otp);

      await signInWithCredential(auth, credential);

      toastsuccessmsg("Login successfully");
      setTimeout(() => {
        setEncodedCookie("token", token, {});
        setEncodedCookie("uid", userId, {});
        setLoading(false);
        navigate("/home");
        formik.resetForm();
      }, 1500);
    } catch (error) {
      setLoading(false);
      toasterrormsg("Invalid OTP");
      console.error("OTP verification error:", error);
    }
  };

  // Opt Sent On mobile number (Firebase v9+)
  const otpSentMobile = async (field, token, userId) => {
    try {
      const verifier = new RecaptchaVerifier(auth, "recaptcha-container", {
        size: "invisible",
      });

      const confirmationResult = await signInWithPhoneNumber(
        auth,
        "+" + field,
        verifier
      );

      toastsuccessmsg("OTP Send Successfully");
      formik.resetForm();
      setTimer(120); // reset to 2 minutes
      setCanResend(true); // 🟢 Enable resend when timer hits 0

      setTimeout(() => {
        navigate("/otpverify", {
          state: {
            verificationId: confirmationResult.verificationId,
            field: field,
            token: token,
            userId: userId,
            isEmailField,
          },
        });
      }, 100);
    } catch (error) {
      toasterrormsg("Failed to send OTP");
      console.error("OTP send error:", error);
    }
  };

  return (
    <>
      <ToastContainer />
      <div id="recaptcha-container"></div>
      <div className="flex flex-col lg:flex-row lg:justify-end lg:min-h-[85vh] xl:min-h-screen h-max">
        {/* Background Section */}
        <div className="h-auto lg:h-screen z-[1] relative lg:fixed top-0 left-0 lg:w-[40%]">
          <AuthBg />
        </div>

        {/* Main Content Section */}
        <div className="lg:w-[60%] py-[5%] px-[5%] md:px-[10%] bg-yellow">
          <div className="flex flex-col lg:flex-row w-full p-10">
            <div className="flex items-center justify-center flex-col lg:flex-row w-full">
              <div className="w-full lg:w-[500px]">
                <h1 className="mb-4 auth-title-y">Verify OTP</h1>
                <p className="mb-[5%] auth-text-g">
                  We have sent a 6-digit code to your registered{" "}
                  {isEmailField ? "email" : "mobile number"}
                </p>

                <form onSubmit={formik.handleSubmit} className="w-full">
                  <div className="w-full mb-[5rem]">
                    {/* OTP Input for Desktop */}
                    <div className="hidden sm:block w-full mb-10">
                      <OTPInput
                        value={formik.values.otp}
                        onChange={handleOtpChange}
                        numInputs={6}
                        inputType="number"
                        renderInput={(props) => <input {...props} />}
                        shouldAutoFocus={true}
                        inputStyle={{
                          justifyContent: "space-around",
                          border: "2px solid #2c3451",
                          outline: "none",
                          borderRadius: "12px",
                          width: "60px",
                          height: "60px",
                          fontSize: "1.8rem",
                          margin: "0 8px",
                          color: "#2c3451",
                          backgroundColor: "#ffffff",
                          fontWeight: "600",
                        }}
                      />
                      {formik.touched.otp && formik.errors.otp && (
                        <div className="mt-5 pl-2 error-msg">
                          {formik.errors.otp}
                        </div>
                      )}
                    </div>

                    {/* OTP Input for Mobile */}
                    <div className="w-full sm:hidden block mb-10">
                      <OTPInput
                        value={formik.values.otp}
                        onChange={handleOtpChange}
                        numInputs={6}
                        inputType="number"
                        renderInput={(props) => <input {...props} />}
                        shouldAutoFocus={true}
                        inputStyle={{
                          justifyContent: "space-around",
                          border: "2px solid #2c3451",
                          outline: "none",
                          borderRadius: "10px",
                          width: "45px",
                          height: "45px",
                          fontSize: "1.5rem",
                          margin: "0 4px",
                          color: "#2c3451",
                          backgroundColor: "#ffffff",
                          fontWeight: "600",
                        }}
                      />
                      {formik.touched.otp && formik.errors.otp && (
                        <div className="mt-5 pl-2 error-msg">
                          {formik.errors.otp}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Timer */}
                  <div className="mb-[3rem] text-center">
                    <p className="auth-text-g font-bold text-2xl">
                      {formatTime()}
                    </p>
                  </div>

                  {/* Resend OTP */}
                  {!canResend && (
                    <div className="text-center mb-[5rem]">
                      <p className="auth-text-g">
                        Didn't get OTP?{" "}
                        <button
                          type="button"
                          className="auth-text-b font-bold underline"
                          onClick={() => {
                            if (isEmailField) {
                              handleResend();
                            } else {
                              otpSentMobile(field, token, userId);
                            }
                          }}
                          disabled={canResend}
                        >
                          Resend OTP
                        </button>
                      </p>
                    </div>
                  )}

                  {/* Submit Button */}
                  <div className="w-full">
                    <div className="w-full justify-center lg:justify-start gap-5 flex mb-8">
                      <button
                        type="submit"
                        className="btn-auth w-full"
                        disabled={loading}
                      >
                        {!loading ? (
                          "Verify OTP"
                        ) : (
                          <div className="flex items-center gap-3 justify-center">
                            <span>Please wait...</span>
                            <ClipLoader color="#ffffff" loading size={20} />
                          </div>
                        )}
                      </button>
                    </div>

                    {/* Back Link */}
                    <div className="w-full text-center">
                      <Link
                        to="/login"
                        className="auth-text-g font-bold hover:underline"
                      >
                        Back to Login
                      </Link>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OtpVerify;
